import React, {useEffect} from 'react';
import {View} from 'react-native';
import {useRoute, useNavigation} from '@react-navigation/native';
import {useDispatch, useSelector} from 'react-redux';
import {
  setScanPackCurrentOrder,
  setCurrentItem,
  reset,
  setMultiBoxAlert,
  setPostScanTrackingInput,
  submitTrackingNumber,
  closePostScanAlertBox,
  hideShipment,
  callShipmentOrder,
  setAliasState,
  addBarcodeSubmit,
  proceedAliasing,
  sameBarcode,
  shareBarcodeMethod,
  assignUniquebarcode,
  setNavigationSource,
  resumePostScanSteps,
  refreshOrderNotes,
  handleReloadOrder,
} from '../../actions/scanpackScanningActions';
import {usePostScanResume} from '../postScanning/usePostScanResume';
import {fetchUserInfo} from '../../actions/userAction';
import SuggestedItemSection from './SuggestedItemSection';
import UnScannedItemsList from './UnScannedItemsList';
import ScannedItemsList from './ScannedItemsList';
import useWidth from '../scanToCartScanning/useWidth';
import useScanPackServices from './hooks/useScanPackServices';
import useOrderChecks from './hooks/useOrderChecks';
import usePackingCamNavigation from './hooks/usePackingCamNavigation';
import InerternetConnectionAlert from '../internetConnectionAlert';
import ScanPackPrompts from './ScanPackPrompts';
import ResponseView from '../scanpack/response_view';
import MultiBoxAlert from '../scanpack/multiBoxAlert';
import ConfirmTrackingNumber from '../scanpack/confirmTrackingNumber';
import AlertBoxTrackingNumber from '../scanpack/alertBoxTrackingNumber';
import AlertBoxShippingLabel from '../scanpack/alertBoxShippingLable';
import PrinterShow from '../directPrinting/printerShow';
import ShipmentRecord from '../scanpack/shipmentsRecord';
import useDirectPrint from './hooks/useDirectPrint';
import useProductAlias from './hooks/useProductAlias';
import {useBoxChampGuidedEffects} from './hooks/useBoxChampGuided';
import BoxChampGuidedBanners from './BoxChampGuidedBanners';
import BoxChampEscalatedLayer from './BoxChampEscalatedLayer';
import AddBarcode from '../scanpack/addBarcode';
import ProductAlias from '../Alias/productAlias';
import TTSEnablementPrompt from '../../commons/modal/ttsEnablementPrompt';

const ScanPackScanning = () => {
  const route = useRoute();
  const navigation = useNavigation();
  const dispatch = useDispatch();
  const windowWidth = useWidth();

  const {data: orderData} = route?.params || {};

  const currentOrder = useSelector(state => state.scanPackScanning.currentOrder);
  const bothSettings = useSelector(state => state.user.bothSettings);
  const userInfo = useSelector(state => state.user.userInfo);
  const scanSuccess = useSelector(state => state.scanPackScanning.scanSuccess);
  const scanError = useSelector(state => state.scanPackScanning.scanError);
  const multiBoxAlert = useSelector(state => state.scanPackScanning.multiBoxAlert);
  const emptyBoxPrint = useSelector(state => state.scanPackScanning.emptyBoxPrint);
  const orderCompleteAction = useSelector(state => state.scanPackScanning.orderCompleteAction);
  const postScanPipeline = useSelector(state => state.scanPackScanning.postScanPipeline);
  const shipment = useSelector(state => state.scanPackScanning.shipment);
  const alias = useSelector(state => state.scanPackScanning.alias);

  const { isConnected, cameraRef } = useScanPackServices(currentOrder, bothSettings);

  useOrderChecks(currentOrder, bothSettings, userInfo);
  usePackingCamNavigation(cameraRef);
  usePostScanResume(params => {
    if (params?.data?.from === 'scanToCart') return false;
    dispatch(resumePostScanSteps());
    return true;
  }, ['postScanningModulesRemaining']);
  const {printerModalVisible, printerList, onPrinterSelect, onCloseModal} = useDirectPrint();
  useProductAlias();
  useBoxChampGuidedEffects();

  useEffect(() => {
    if (orderData?.order) {
      dispatch(setScanPackCurrentOrder(orderData?.order));
      dispatch(setCurrentItem());
    }

    if (orderData?.from === 'orderDetailPage') {
      dispatch(handleReloadOrder());
      dispatch(refreshOrderNotes());
    }
    
    dispatch(setNavigationSource({ from: orderData?.from, routeParams: route?.params }));
  }, [orderData]);

  useEffect(() => {
    dispatch(fetchUserInfo());
    return () => {
      dispatch(reset());
    };
  }, []);

  const isTrackingStepActive = postScanPipeline.active &&
    (postScanPipeline.currentStep === 'Record' || postScanPipeline.currentStep === 'Verify');

  return (
    <View style={{height: '100%', flex: 1}}>
      {!isConnected && (
        <View style={{position: 'absolute', top: '20%', zIndex: 99}}>
          <InerternetConnectionAlert />
        </View>
      )}

      {multiBoxAlert && (
        <MultiBoxAlert
          emptyBoxPrint={emptyBoxPrint}
          onCloseAlertMultiBox={() => dispatch(setMultiBoxAlert(false))}
        />
      )}

      {printerModalVisible && (
        <PrinterShow
          dataPrinter={printerList}
          handlePrinterSelection={(printer) => onPrinterSelect(printer)}
          closePrompt={onCloseModal}
        />
      )}

      {/* TODO: ShipmentRecord was imported but never rendered in old scanpack_item.js — decide whether to keep or remove */}
      {shipment.visible && (
        <View style={{position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, zIndex: 9998, justifyContent: 'center', alignItems: 'center', backgroundColor: 'rgba(0,0,0,0.5)'}}>
          <ShipmentRecord
            Shipment={shipment.data}
            errorMessageShow={shipment.errorMessageShow}
            errorMessage={shipment.errorMessage}
            hideShipment={() => dispatch(hideShipment())}
            callOrder={(order) => dispatch(callShipmentOrder(order))}
          />
        </View>
      )}

      {alias.addBarcodeWindow && (
        <AddBarcode
          addBarcodeWindow={alias.addBarcodeWindow}
          addBarcodeText={alias.addBarcodeText}
          addBarcodeChange={(text) => dispatch(setAliasState({ addBarcodeText: text }))}
          addBarcodeSubmit={() => dispatch(addBarcodeSubmit())}
          closeAlert={() => dispatch(setAliasState({ addBarcodeWindow: false, addBarcodeText: '', addBarcodeItem: null }))}
        />
      )}

      {(alias.aliasVisible || alias.shareBarcodeShow) && (
        <ProductAlias
          alias={alias.aliasVisible}
          shareBarcodeShow={alias.shareBarcodeShow}
          Product={alias.productData}
          updateProduct={alias.productData}
          windowWidth={windowWidth}
          proceedAliasing={() => dispatch(proceedAliasing())}
          shareBarcodeMethod={() => dispatch(shareBarcodeMethod())}
          sameBarcode={() => dispatch(sameBarcode())}
          assignUniquebarcode={() => dispatch(assignUniquebarcode())}
        />
      )}

      <ScanPackPrompts />

      <TTSEnablementPrompt />

      {(scanSuccess || scanError || orderCompleteAction) && (
        <View style={{position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, zIndex: 10000}}>
          <ResponseView
            orderCompleteAction={orderCompleteAction}
            scanSuccessAction={scanSuccess}
            scanFailAction={scanError}
          />
        </View>
      )}

      {isTrackingStepActive && (
        <View style={{position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, zIndex: 9999}}>
          {postScanPipeline.alertBoxTracking && (
            <AlertBoxTrackingNumber
              trackingOrderInput={postScanPipeline.trackingInput}
              onChangeText={(text) => dispatch(setPostScanTrackingInput(text))}
              onSubmitEditing={() => dispatch(submitTrackingNumber())}
              closeAlert={() => dispatch(closePostScanAlertBox('alertBoxTracking'))}
            />
          )}
          {postScanPipeline.alertBoxShipping && (
            <AlertBoxShippingLabel
              trackState={postScanPipeline.trackState}
              orderConfirmationPer={postScanPipeline.orderConfirmationPer}
              editPermissionCheck={userInfo?.role?.add_edit_order_items}
              order={currentOrder}
              trackingOrderInput={postScanPipeline.trackingInput}
              onChangeText={(text) => dispatch(setPostScanTrackingInput(text))}
              onSubmitEditing={() => dispatch(submitTrackingNumber())}
              closeAlert={() => dispatch(closePostScanAlertBox('alertBoxShipping'))}
              navigation={navigation}
            />
          )}
          <ConfirmTrackingNumber
            postScanningFieldLabel={postScanPipeline.fieldLabel}
            postScanningOrderNumber={postScanPipeline.orderNumber}
            statecheck={postScanPipeline.verifyNoTracking}
            alertBoxTracking={postScanPipeline.alertBoxTracking}
            alertBoxShipping={postScanPipeline.alertBoxShipping}
            postScanningMessageDetail={postScanPipeline.messageDetail}
            trackingOrderInput={postScanPipeline.trackingInput}
            onChangeText={(text) => dispatch(setPostScanTrackingInput(text))}
            onSubmitEditing={() => dispatch(submitTrackingNumber())}
            order={currentOrder}
          />
        </View>
      )}

      <BoxChampGuidedBanners />

      <View style={{flex: 1, flexDirection: 'row'}}>
        <View
          style={{
            flex: 2,
            backgroundColor: '#4A6179',
            display: windowWidth >= 900 ? 'flex' : 'none'
          }}>
          <UnScannedItemsList />
        </View>

        <SuggestedItemSection />

        <View
          style={{
            flex: 2,
            zIndex: -1,
            backgroundColor: '#4A6179',
            display: windowWidth >= 900 ? 'flex' : 'none'
          }}>
          <ScannedItemsList />
        </View>
      </View>

      <BoxChampEscalatedLayer />
    </View>
  );
};

export default ScanPackScanning;
